// ---------------------------------------------------------------------------
// Digital Inspection — Change Log (Round 7 as "Audit Log"; renamed and
// permission-scoped in Rounds DI-1/DI-2)
//
// Read-only view of Digital Inspection change entries (ActionType "DI.*"):
// supervisor overrides (cell edits, close-job), entry deletes, confirmed
// nonconformities (Scrap / Leave as is / File DMR, incl. the Epicor DMR
// creation status), and document upload/delete.
//
// ACCESS (Round DI-2): the "DI - Change Log" permission (gate di.changeLog)
// — grant it to the quality-engineer role or specific people in Access
// Management. The log is SCOPED to a job / PO: the page opens pre-filtered
// to the job loaded in Digital Inspection (?job=...), and the API rejects
// unscoped queries from non-admin holders. The backend route name
// (/Inspection/audit-log) and the dbo.AuditEntries table are unchanged by
// design — only the visible name is "Change Log".
//
// Reads GET /Inspection/audit-log?from=&to=&actor=&action=&job=&limit=
// ---------------------------------------------------------------------------
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Container, Card, CardBody } from "reactstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../api";
import "./di-theme.css";

// Friendly labels for the known DI action types.
const ACTION_LABELS = {
  "DI.SupervisorOverride.Cell": "Supervisor override — cell value",
  "DI.SupervisorOverride.CloseJob": "Supervisor override — close job",
  "DI.SupervisorOverride.DeleteEntry": "Supervisor delete — saved value",
  "DI.NonConformance": "Nonconformity confirmed",
  "DI.Attachment.Upload": "Document uploaded",
  "DI.Attachment.Delete": "Document deleted",
};

const ACTION_OPTIONS = [
  { value: "", label: "All actions" },
  { value: "DI.SupervisorOverride.Cell", label: "Override — cell value" },
  { value: "DI.SupervisorOverride.CloseJob", label: "Override — close job" },
  { value: "DI.SupervisorOverride.DeleteEntry", label: "Delete — saved value" },
  { value: "DI.NonConformance", label: "Nonconformity (Scrap / As-is / DMR)" },
  { value: "DI.Attachment.Upload", label: "Document uploaded" },
  { value: "DI.Attachment.Delete", label: "Document deleted" },
];

const labelFor = (a) => ACTION_LABELS[a] || a;

// Local datetime for an ISO/UTC timestamp.
const fmtWhen = (utc) => {
  if (!utc) return "";
  const d = new Date(utc);
  if (isNaN(d.getTime())) return String(utc);
  return d.toLocaleString();
};

const AuditLog = () => {
  const navigate = useNavigate();
  // Round DI-2 — job/PO scope. Pre-filled from the ?job= query string the
  // Digital Inspection page passes when the Change Log is opened for a
  // loaded job / PO context.
  const [searchParams] = useSearchParams();
  const [job, setJob] = useState(searchParams.get("job") || "");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [needsJob, setNeedsJob] = useState(false);

  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [actor, setActor] = useState("");
  const [action, setAction] = useState("");
  const [limit, setLimit] = useState(200);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    setNeedsJob(false);
    try {
      const params = {};
      if (from) params.from = new Date(from).toISOString();
      if (to) params.to = new Date(to).toISOString();
      if (actor.trim()) params.actor = actor.trim();
      if (action) params.action = action;
      if (job.trim()) params.job = job.trim();
      params.limit = Number(limit) || 200;

      const res = await api.get("/Inspection/audit-log", { params });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        setError(
          "You don't have access to the Change Log. Ask an administrator to grant the 'DI - Change Log' permission to your role or user."
        );
      } else if (status === 400 && !job.trim()) {
        // Round DI-2 — unscoped queries are limited to administrators:
        // ordinary Change Log holders must select a job / PO context.
        setNeedsJob(true);
        setError(
          err?.response?.data?.error ||
            "Select a valid job / PO context to view its Change Log."
        );
      } else {
        setError(
          err?.response?.data?.error ||
            "Couldn't load the Change Log. Try again."
        );
      }
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [from, to, actor, action, job, limit]);

  // Initial load.
  useEffect(() => {
    load();
   
  }, []);

  const clearFilters = () => {
    setFrom("");
    setTo("");
    setActor("");
    setAction("");
    setLimit(200);
    // The job/PO scope is intentionally kept — it is the context being
    // reviewed, not a cosmetic filter.
  };

  const summary = useMemo(() => {
    if (loading) return "Loading…";
    if (error) return "";
    return `${rows.length} ${rows.length === 1 ? "entry" : "entries"}`;
  }, [rows.length, loading, error]);

  return (
    <React.Fragment>
      <div className="page-content di-module">
        <Container fluid>
          <Card>
            <CardBody>
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-1">
                <div>
                  <h5 className="mb-1">
                    Digital Inspection — Change Log
                    {job.trim() ? (
                      <span className="text-muted fw-normal">
                        {" "}
                        · {job.trim()}
                      </span>
                    ) : null}
                  </h5>
                  <p className="text-muted small mb-0">
                    Supervisor overrides, deletes, nonconformities (incl.
                    Epicor DMR status), and document activity for the job /
                    PO being reviewed. Most recent first.
                  </p>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted small">{summary}</span>
                  <button
                    type="button"
                    className="di-btn di-btn--ghost"
                    onClick={() => navigate("/di")}
                  >
                    <i className="ri-arrow-left-line" />
                    Back to inspection
                  </button>
                </div>
              </div>

              {/* Filters */}
              <div
                className="d-flex flex-wrap align-items-end gap-2 my-3"
                style={{ rowGap: 8 }}
              >
                <div>
                  <label className="text-muted small d-block mb-1">
                    Job / PO
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="e.g. 0094362"
                    value={job}
                    onChange={(e) => setJob(e.target.value)}
                    style={{ width: 140 }}
                  />
                </div>
                <div>
                  <label className="text-muted small d-block mb-1">From</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={from}
                    onChange={(e) => setFrom(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-muted small d-block mb-1">To</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-muted small d-block mb-1">Action</label>
                  <select
                    className="form-select form-select-sm"
                    value={action}
                    onChange={(e) => setAction(e.target.value)}
                  >
                    {ACTION_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-muted small d-block mb-1">
                    Actor Emp ID
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="e.g. 3325"
                    value={actor}
                    onChange={(e) => setActor(e.target.value)}
                    style={{ width: 120 }}
                  />
                </div>
                <div>
                  <label className="text-muted small d-block mb-1">Limit</label>
                  <select
                    className="form-select form-select-sm"
                    value={limit}
                    onChange={(e) => setLimit(Number(e.target.value))}
                  >
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                    <option value={500}>500</option>
                    <option value={1000}>1000</option>
                  </select>
                </div>

                <button
                  type="button"
                  className="di-btn di-btn--primary"
                  onClick={load}
                  disabled={loading}
                >
                  <i className="ri-search-line" />
                  {loading ? "Loading…" : "Apply"}
                </button>
                <button
                  type="button"
                  className="di-btn di-btn--ghost"
                  onClick={() => {
                    clearFilters();
                  }}
                  disabled={loading}
                >
                  <i className="ri-refresh-line" />
                  Reset
                </button>
              </div>

              {error ? (
                <div className="alert alert-warning mb-0" role="alert">
                  {error}
                  {needsJob ? (
                    <div className="small mt-1">
                      Enter the Job / PO above (or open the Change Log from a
                      loaded job in Digital Inspection) and press Apply.
                    </div>
                  ) : null}
                </div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table className="table table-sm align-middle mb-0">
                    <thead>
                      <tr className="text-muted">
                        <th style={{ whiteSpace: "nowrap" }}>When</th>
                        <th>Who</th>
                        <th>Action</th>
                        <th>Reference</th>
                        <th>Detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 && !loading ? (
                        <tr>
                          <td colSpan={5} className="text-center text-muted py-4">
                            No change-log entries match these filters yet.
                          </td>
                        </tr>
                      ) : (
                        rows.map((r) => (
                          <tr key={r.id}>
                            <td style={{ whiteSpace: "nowrap" }}>
                              {fmtWhen(r.whenUtc)}
                            </td>
                            <td>
                              {r.actorName ? (
                                <span>
                                  {r.actorName}{" "}
                                  <span className="text-muted">
                                    ({r.actorEmpId})
                                  </span>
                                </span>
                              ) : (
                                r.actorEmpId
                              )}
                            </td>
                            <td>{labelFor(r.actionType)}</td>
                            <td style={{ maxWidth: 280, wordBreak: "break-word" }}>
                              {r.reference}
                            </td>
                            <td style={{ maxWidth: 360, wordBreak: "break-word" }}>
                              {r.detail}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default AuditLog;
